/* Membership Type Module */
import { ProductsMemberships } from '../../../entities/pool/public/products_memberships';
import { Session } from 'express-session';
import { MembershipWithdraws } from '../../../entities/pool/public/membership_withdraws';
import { MembershipAdjustments } from '../../../entities/pool/public/membership_adjustments';

export type MembershipAuthKeyResType = {
  code: string;
  data: {
    token: string;
    expiredAt: string;
  };
};

export type CreateMembershipProjectResType = {
  code: '000' | '001' | '003';
  data: {
    id?: string;
  };
};

export type CreateSupportPaymentResType = {
  code: '000' | '001' | '003';
  data: {
    supportMessage: {
      id: 'string';
    };
    portone: {
      merchantUid: 'string';
      channelKey: 'string';
      storeId: 'string';
    };
  };
};

export type GetOrCreateCietyResType = {
  code: '000' | '001' | '033' | '034' | '035' | '036';
  data: {
    id: string;
    displayName: string;
    subdomain: string;
  };
};

export type DeleteCietyResType = {
  code: '000' | '001' | '033' | '034' | '037';
  data: null;
};

export interface MembershipInfo {
  is_membership: boolean;
  need_signup_membership: boolean;
}

export type ProductMembership = Pick<ProductsMemberships, 'product_id' | 'membership_id'>;

interface CommissionData {
  count: number;
  guideImage: string;
  message: string;
}

interface ThumbnailImage {
  url: string;
  width: number;
  height: number;
}

interface Payment {
  id: string;
  type: 'SUBSCRIPTION_PASS';
  price: number;
  currency: 'KRW';
  period: number;
}

interface Creator {
  id: string;
  state: 'CREATE' | 'DELETE';
  displayName: string;
  imageUrl: string;
}

export const MEMBERSHIP_PRODUCT_TYPE = {
  GOODS: 'GOODS',
  ORDER_MADE: 'ORDER_MADE',
  DIGITAL_GOODS: 'DIGITAL_GOODS',
} as const;

// TODO: product reward 라는 의미 담기
export const MEMBERSHIP_BUY_PRODUCT_TYPE = {
  BUY_DIGITAL_GOODS: `BUY_${MEMBERSHIP_PRODUCT_TYPE.DIGITAL_GOODS}`,
  BUY_GOODS: `BUY_${MEMBERSHIP_PRODUCT_TYPE.GOODS}`,
  BUY_ORDER_MADE: `BUY_${MEMBERSHIP_PRODUCT_TYPE.ORDER_MADE}`,
} as const;

export type MembershipProductType = (typeof MEMBERSHIP_PRODUCT_TYPE)[keyof typeof MEMBERSHIP_PRODUCT_TYPE];

// 상품에 대한 리워드 타입
export type ProductRewardType =
  (typeof MEMBERSHIP_BUY_PRODUCT_TYPE)[keyof typeof MEMBERSHIP_BUY_PRODUCT_TYPE];

export const REWARD_TYPES = {
  BUY_DIGITAL_GOODS: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_DIGITAL_GOODS,
  BUY_GOODS: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_GOODS,
  BUY_ORDER_MADE: MEMBERSHIP_BUY_PRODUCT_TYPE.BUY_ORDER_MADE,
  COMMISSION: 'COMMISSION',
  COMMUNITY: 'COMMUNITY',
  DIGITAL_CONTENT: 'DIGITAL_CONTENT',
  DM: 'DM',
  LIVE_STREAMING: 'LIVE_STREAMING',
  ONLINE_CLASS: 'ONLINE_CLASS',
  CHANNEL: 'CHANNEL',
  CUSTOM: 'CUSTOM',
} as const;

export const MEMBERSHIP_STATE = {
  /**
   * 비공개
   */
  HIDDEN: 'HIDDEN',

  /**
   * 공개
   */
  OPEN: 'OPEN',

  CLOSED: 'CLOSED',
  DELETED: 'DELETED',
  DELETION_PENDING: 'DELETION_PENDING',
} as const;

export type MembershipStateType = keyof typeof MEMBERSHIP_STATE;

// RewardType 타입 정의
export type RewardType = (typeof REWARD_TYPES)[keyof typeof REWARD_TYPES];

interface Reward {
  id: string;
  type: RewardType;
  title: string;
  data?: CommissionData; // COMMISSION type일 때만 존재
}

export interface MembershipSummary {
  id: string;
  displayName: string;
  index: number;
  state: MembershipStateType;
  status: 'JOINED' | 'EXPIRED'; // 다른 status 값들이 있다면 추가 필요
}

export interface Membership {
  id: string;
  displayName: string;
  index: number;
  state: MembershipStateType;
  isRecommended: boolean;
  projectId: string;
  description: string;
  rewards: Reward[];
  thumbnailImage: ThumbnailImage;
  payment: Payment;
  isActive: boolean;
  joinedAt: string;
  expiredAt: string | null;
  streakCount: number;
  status: 'JOINED' | 'EXPIRED'; // 다른 status 값들이 있다면 추가 필요
  creator: Creator;
  memberCount: number;
  isTermsChecked: boolean;
}

export type PartialMembership = Pick<Membership, 'id' | 'displayName' | 'state' | 'index'> & {
  rewards: Pick<Reward, 'type'>[];
};

export interface ProductDetailMembershipInfo {
  is_membership: boolean;
  need_signup_membership: boolean;
  memberships?: PartialMembership[];
}

export type QueriedMembershipCreator = PartialMembership & {
  rewards: {
    type: ProductRewardType;
  }[];
};

export type CheckedRewardType = Extract<
  RewardType,
  'BUY_GOODS' | 'BUY_ORDER_MADE' | 'BUY_DIGITAL_GOODS' | 'CHANNEL'
>;

// 멤버십 상품에 접근 권한 없음
export const HAS_NOT_MEMBERSHIP_PERMISSION = 'HAS_NOT_MEMBERSHIP_PERMISSION' as const;

// 멤버십 상품의 날짜가 만료됨
export const EXPIRED_PERMISSION = 'EXPIRED_PERMISSION' as const;

export const AXIOS_TIME_OUT = 'AXIOS_TIME_OUT' as const;
// 멤버십을 가입하지 않음
type HAS_NOT_MEMBERSHIP_TYPE = typeof HAS_NOT_MEMBERSHIP_PERMISSION;

// 멤버십 기간이 만료됨
export type EXPIRED_PERMISSION_TYPE = typeof EXPIRED_PERMISSION;

export type MembershipExpiredInfoValueType = {
  expiredAt: string | EXPIRED_PERMISSION_TYPE;
  joinedAt: string;
};

// 이건 의미적으로 이렇다는거고 실제 동작은 당연히 string 이다..
// `${number}` 이거 썼었는데, 아직 ts가 바보라서 잘 못해준다.
export type NumericString = string;

export type ExpiredAtMembershipType = {
  id: NumericString;
  // utc 형식의 string
  expiredAt: string;
  joinedAt: string;
}[];

export type IsRewardsIncludedReturnType =
  | {
      success: true;
      data: ExpiredAtMembershipType;
    }
  | {
      success: false;
      error_code?: typeof AXIOS_TIME_OUT;
    };

export type MembershipExpiredInfo = Record<NumericString, MembershipExpiredInfoValueType>;

export interface ProductMembershipPermission {
  main_membership_id: NumericString | HAS_NOT_MEMBERSHIP_TYPE;
  membership_expired_info?: MembershipExpiredInfo;
}

export interface RequestWithSession extends Request {
  session: Session & {
    product_membership_permission?: ProductMembershipPermission;
    authorization_code: string;
  };
}

export type MembershipPermissionCheckReturnType = {
  success: boolean;
  product_membership_permission?: ProductMembershipPermission;
};

export type ContactButtonClickType =
  | {
      type: 'MEMBERSHIP';
      membership_id?: string;
      support_id?: never;
      commission_request_id?: never;
      validationHook?: () => boolean | Promise<boolean>;
    }
  | {
      type: 'SUPPORT';
      support_id?: string;
      membership_id?: never;
      commission_request_id?: never;
      validationHook?: () => boolean | Promise<boolean>;
    }
  | {
      type: 'COMMISSION';
      commission_request_id?: string;
      support_id?: never;
      membership_id?: never;
      validationHook?: () => boolean | Promise<boolean>;
    };

export interface MembershipResponse<T = Membership> {
  success: boolean;
  message?: string;
  data: T;
}

export interface SearchQueryItem {
  id: string;
  displayName: string;
  state: Membership['state'];
}

export type MembershipServiceType_Membership = 'MEMBERSHIP';
export type MembershipServiceType_Support = 'SUPPORT';
export type MembershipServiceType_Commission = 'COMMISSION';
export type MembershipAdjustmentType = 'PAYMENT' | 'REFUND';
export type MembershipAdjustmentTargetType = 'CREATOR' | 'CREW';
export type MembershipAdjustmentServiceType =
  | MembershipServiceType_Membership
  | MembershipServiceType_Support
  | MembershipServiceType_Commission;

export type MembershipAdjustment = {
  id: number;
  amount: string; // 정산 금액
  created_at: Date;
  crew_id: number | null; // 플러스가 받는 정산일 경우, crew_id 기록
  fee: string; // 마플샵에서 가져가는 수수료
  type: MembershipAdjustmentType;
  target: MembershipAdjustmentTargetType;
  membership_name: string | null;
  support_name: string | null;
  commission_name: string | null;
  service: MembershipAdjustmentServiceType;
  statement_id: string;
};

type MembershipAdjustmentFullBase = {
  id: number;
  amount: string; // 정산 금액
  creator_membership_adjustment_id: number | null; // 플러스 정산금일 경우, 해당 정산금이 어디랑 분배되어 있는지를 구분하기 위함
  created_at: Date;
  crew_id: number | null; // 플러스가 받는 정산일 경우, crew_id 기록
  fee: string; // 마플샵에서 가져가는 수수료
  membership_project_id: number; // 멤버쉽, 후원 프로젝트 id
  membership_withdraw_id: number | null; // 연결된 출금 id
  statement_id: number; // 멤버십 서비스쪽에서 주는 결제 단위 id
  type: MembershipAdjustmentType;
  target: MembershipAdjustmentTargetType;
};

export type MembershipAdjustmentFull =
  | (MembershipAdjustmentFullBase & {
      membership_id: number | null;
      membership_name: string | null;
      service: MembershipServiceType_Membership;
    })
  | (MembershipAdjustmentFullBase & {
      support_id: number | null;
      support_name: string | null;
      service: MembershipServiceType_Support;
    });

export type MembershipAdjustmentAtList = MembershipAdjustment & {
  without_commission_amount: string;
  pg_amount: string;
};

export type MembershipWithdrawSummary = Omit<MembershipWithdraws, 'account_history'>;

export type MembershipAdjustmentShareRatio = {
  membership_project_id: number;
  creator_ratio: number; // [note: '0 ~ 1 사이, 크리에이터가 가져가는 비율']
};

export interface GetMembershipAdjustmentsParams {
  start_date?: string | Date;
  end_date?: string | Date;
  membership_project_id?: string;
  membership_id?: number;
  type?: MembershipAdjustmentType;
  service?: MembershipAdjustmentServiceType;
  target?: MembershipAdjustmentTargetType;
  statement_ids?: string[];
  request_ids?: string[];
  crew_id?: number;
  not_withdraw?: true;
}

export interface GetMembershipWithdrawsParams {
  membership_project_id?: string;
  target_crew_id?: number;
  source_crew_id?: number;
  start_date?: string | Date;
  end_date?: string | Date;
  withdraw_subject: 'crew' | 'creator';
  status?: 'pending' | 'cancel' | 'approve' | 'payment';
  ids?: number[];
}

export type CreateMembershipWithdrawParams = Pick<
  MembershipWithdraws,
  | 'amount'
  | 'target_crew_id'
  | 'source_crew_id'
  | 'user_id'
  | 'membership_project_id'
  | 'membership_amount'
  | 'support_amount'
>;

export type CreateMembershipAdjustmentParams = Omit<MembershipAdjustments, 'id' | 'created_at'>;

export type MembershipWithdrawProfitSummary = {
  id: number;
  mw_total_profit: number;
  mw_total_fee: number;
  mw_total_pg_commission: number;
};
